
import React from "react";
import { Card } from "@/components/ui/card";

// Custom components for Kit Consulting page with width fixes
const KitTitle = ({ children }: { children: React.ReactNode }) => {
  return (
    <h3 className="text-xl md:text-2xl font-semibold text-left w-full mb-4 font-['fd-l']">
      {children}
    </h3>
  );
};

const KitText = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className="w-full text-base text-left opacity-80">
      {children}
    </div>
  );
};

const KitConsultingAbout = () => {
  return (
    <div id="about" className="w-full px-4 py-8" style={{ paddingTop: "10px" }}>
      <div className="w-full">
        <div className="w-full max-w-none bg-[#0d141f] text-center mb-10 p-10 rounded-xl">
          <div>
            <div className="buttonnn wow animate__animated animate__fadeIn inline-flex">
              <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 12 12" fill="none">
                <circle cx="6.06896" cy="5.9508" r="5.484" fill="#EEFF30" />
              </svg> Subvención
            </div>
          </div>
          <div className="text-3xl md:text-4xl font-bold mt-4 text-white font-['aeo-r']">
            Llevando tu negocio a <span className="yellow">siguientes niveles</span>
          </div>
        </div>
      </div>

      {/* Service cards with transparent background and dark stroke */}
      <div className="w-full space-y-8 py-4">
        {/* First Row */}
        <Card className="w-full rounded-xl overflow-hidden bg-transparent border border-[#0d141f] shadow-none">
          <div className="flex flex-col md:flex-row p-4 md:p-6 w-full">
            <div className="w-full md:w-1/3 flex-shrink-0 mb-4 md:mb-0">
              <img src="/img/datos1.jpg" alt="Análisis de Datos Básico" className="object-cover w-full h-auto rounded-lg" />
            </div>
            <div className="w-full md:w-2/3 md:pl-10">
              <KitTitle>Servicio de Asesoramiento en Análisis de Datos (Básico)</KitTitle>
              <KitText>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Identificamos los datos críticos del negocio, como facturación, flujos de caja, cobros y pagos, movimientos contables, ventas y stock, para ayudar al cliente a entender mejor la situación de su empresa.</li>
                  <li>Organizamos la información financiera y operativa para que el cliente tenga un primer orden claro y comprensible.</li>
                  <li>Construimos reportings en Excel que permitan visualizar la evolución de las finanzas de la pyme: cash flow, balances mensuales y control de ingresos y gastos.</li>
                  <li>Asesoramos al cliente sobre cómo interpretar la información recogida en los reportings que preparamos, facilitando su uso en la toma de decisiones del día a día.</li>
                  <li>Realizamos conciliaciones manuales de cobros y pagos con plataformas como Shopify o Stripe para verificar que los movimientos encajan con la contabilidad.</li>
                  <li>Identificamos áreas de mejora en la gestión de la información financiera de la pyme, aportando recomendaciones prácticas para optimizar la organización de los datos contables y operativos.</li>
                </ul>
              </KitText>
            </div>
          </div>
        </Card>

        {/* Second Row */}
        <Card className="w-full rounded-xl overflow-hidden bg-transparent border border-[#0d141f] shadow-none">
          <div className="flex flex-col md:flex-row p-4 md:p-6 w-full">
            <div className="w-full md:w-1/3 flex-shrink-0 mb-4 md:mb-0">
              <img src="/img/datos2.jpg" alt="Análisis de Datos Avanzado" className="object-cover w-full h-auto rounded-lg" />
            </div>
            <div className="w-full md:w-2/3 md:pl-10">
              <KitTitle>Servicio de Asesoramiento en Análisis de Datos (Avanzado)</KitTitle>
              <KitText>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Evaluamos la calidad y coherencia de los datos existentes para que soporten análisis financieros y operativos avanzados, orientados a la toma de decisiones estratégicas.</li>
                  <li>Integramos múltiples fuentes internas y externas para consolidar la información y obtener una visión completa y actualizada del negocio.</li>
                  <li>Desarrollamos modelos financieros dinámicos que permiten simular distintos escenarios de crecimiento o necesidades de financiación futura.</li>
                  <li>Preparamos entregables financieros avanzados y reporting específico para inversores, asegurando que la pyme cuenta con la documentación adecuada para comunicar de forma clara sus métricas clave.</li>
                </ul>
              </KitText>
            </div>
          </div>
        </Card>

        {/* Third Row */}
        <Card className="w-full rounded-xl overflow-hidden bg-transparent border border-[#0d141f] shadow-none">
          <div className="flex flex-col md:flex-row p-4 md:p-6 w-full">
            <div className="w-full md:w-1/3 flex-shrink-0 mb-4 md:mb-0">
              <img src="/img/procesos.jpg" alt="Asesoramiento en Procesos de Negocio" className="object-cover w-full h-auto rounded-lg" />
            </div>
            <div className="w-full md:w-2/3 md:pl-10">
              <KitTitle>Servicio de Asesoramiento en Procesos de Negocio o Proceso de Producción</KitTitle>
              <KitText>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Analizamos los procesos internos de gestión financiera y operativa de la pyme para detectar ineficiencias o desorganización que puedan estar afectando al rendimiento global del negocio.</li>
                  <li>Revisamos los flujos contables y de tesorería para asegurar que los procedimientos actuales permiten un control adecuado de la liquidez y las obligaciones financieras de la empresa.</li>
                  <li>Proponemos mejoras prácticas en la estructuración de procesos financieros, como la calendarización de cierres contables, preparación ordenada de entregables mensuales y flujos de revisión financiera periódica.</li>
                  <li>Establecemos rutinas y flujos de trabajo que permiten al equipo fundador mantener bajo control la documentación clave del negocio, asegurando que la información está siempre disponible y actualizada.</li>
                  <li>Apoyamos la preparación de documentación operativa y financiera para interlocutores externos, como inversores o auditores, facilitando la presentación clara y ordenada del estado de la pyme.</li>
                  <li>Identificamos puntos críticos en la operativa diaria del negocio, proponiendo soluciones sencillas para mejorar la eficiencia y la trazabilidad de los datos financieros y operativos.</li>
                </ul>
              </KitText>
            </div>
          </div>
        </Card>

        {/* Fourth Row */}
        <Card className="w-full rounded-xl overflow-hidden bg-transparent border border-[#0d141f] shadow-none">
          <div className="flex flex-col md:flex-row p-4 md:p-6 w-full">
            <div className="w-full md:w-1/3 flex-shrink-0 mb-4 md:mb-0">
              <img src="/img/estrategia.jpg" alt="Estrategia y Rendimiento de Negocio" className="object-cover w-full h-auto rounded-lg" />
            </div>
            <div className="w-full md:w-2/3 md:pl-10">
              <KitTitle>Servicio de Asesoramiento en Estrategia y Rendimiento de Negocio</KitTitle>
              <KitText>
                <ul className="list-disc pl-5 space-y-2">
                  <li>Analizamos la situación actual de la pyme desde una perspectiva estratégica y financiera, detectando debilidades en la estructura de costes, márgenes y eficiencia operativa.</li>
                  <li>Asesoramos a los fundadores en la definición de prioridades estratégicas del negocio, identificando qué métricas y objetivos financieros deben monitorizarse para mantener el foco en el crecimiento sostenible.</li>
                  <li>Preparamos documentación estratégica y financiera que ayuda a la pyme a comunicar de forma clara su posicionamiento competitivo ante inversores o socios potenciales.</li>
                  <li>Aportamos recomendaciones prácticas para estructurar la toma de decisiones del equipo directivo, basándonos en datos financieros y operativos clave del negocio.</li>
                  <li>Acompañamos al equipo fundador en la revisión periódica de la estrategia, asegurando que los indicadores de rendimiento y la evolución financiera estén alineados con los objetivos de crecimiento.</li>
                  <li>Identificamos oportunidades de mejora en la estructura económica y operativa de la empresa, proponiendo acciones concretas para reforzar la rentabilidad y eficiencia del negocio.</li>
                  <li>Apoyamos la preparación de materiales clave como business plans, reportings analíticos y paneles de control que ofrecen una visión clara de la evolución del negocio frente a sus objetivos estratégicos.</li>
                </ul>
              </KitText>
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default KitConsultingAbout;
